import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Minus, Trash2, ShoppingBag } from 'lucide-react';
import { useCartStore } from '../store/useCartStore';
import { formatPrice } from '../lib/formatPrice';
import { usePaymentSettings } from '../lib/usePaymentSettings';

interface CartDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const CartDrawer: React.FC<CartDrawerProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { items, removeItem, updateQuantity, total } = useCartStore();
  const { settings: paymentSettings } = usePaymentSettings();
  const priceOptions = {
    currencySymbol: paymentSettings.currencySymbol,
    locale: paymentSettings.priceLocale,
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-brand-ink/40 backdrop-blur-sm z-[60]"
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed top-0 right-0 h-full w-full max-w-md bg-brand-paper z-[70] shadow-2xl flex flex-col"
          >
            <div className="p-6 border-b border-brand-ink/5 flex justify-between items-center">
              <h2 className="text-xl font-serif text-brand-ink flex items-center gap-2">
                <ShoppingBag className="w-5 h-5" />
                {t('cart.title')}
              </h2>
              <button onClick={onClose} className="p-2 hover:bg-brand-stone rounded-full transition-colors">
                <X className="w-6 h-6 text-brand-ink" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {items.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-brand-ink/40 space-y-4">
                  <ShoppingBag className="w-12 h-12 opacity-20" />
                  <p className="font-sans">{t('cart.empty')}</p>
                </div>
              ) : (
                items.map((item) => (
                  <div key={`${item.productId}-${item.variantId ?? 'none'}`} className="flex gap-4 pb-6 border-b border-brand-ink/5">
                    <div className="w-20 h-20 bg-brand-stone overflow-hidden">
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between mb-1">
                        <h3 className="text-sm font-serif text-brand-ink">{item.name}</h3>
                        <button onClick={() => removeItem(item.productId, item.variantId)} className="text-brand-ink/30 hover:text-brand-cinnabar transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      {item.variantId && (
                        <p className="text-[10px] text-brand-gold font-sans mb-3">{item.variantId}</p>
                      )}
                      <div className="flex justify-between items-center">
                        <div className="flex items-center border border-brand-ink/10 rounded">
                          <button
                            onClick={() => updateQuantity(item.productId, item.variantId, item.quantity - 1)}
                            className="p-1 hover:bg-brand-stone transition-colors"
                          >
                            <Minus className="w-3 h-3" />
                          </button>
                          <span className="px-3 text-xs font-sans">{item.quantity}</span>
                          <button
                            onClick={() => updateQuantity(item.productId, item.variantId, item.quantity + 1)}
                            className="p-1 hover:bg-brand-stone transition-colors"
                          >
                            <Plus className="w-3 h-3" />
                          </button>
                        </div>
                        <span className="text-sm font-serif text-brand-ink">{formatPrice(item.price * item.quantity, priceOptions)}</span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

            {items.length > 0 && (
              <div className="p-6 bg-brand-stone/30 border-t border-brand-ink/5 space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-sans text-brand-ink/60">{t('cart.total')}</span>
                  <span className="text-xl font-serif text-brand-ink">{formatPrice(total(), priceOptions)}</span>
                </div>
                <Link
                  to="/checkout"
                  onClick={onClose}
                  className="block w-full py-4 text-center bg-green-600 text-white text-sm font-sans tracking-widest rounded-full hover:bg-green-700 transition-colors shadow-md hover:shadow-lg"
                >
                  {t('cart.checkout')}
                </Link>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;
